import { useTranslation } from 'react-i18next';
import {
    Banner,
    BannerDescription,
    BannerImage,
    BannerImageContainer,
    BannerLinks,
    BannerText,
    BannerTextContainer,
    BannerTitle,
    Button,
    FluidImage,
    Link,
    SquareImageContainer,
} from '@vp/swan';
import { useStyles } from '@vp/ubik-context';
import { useUrls } from '../../../../contexts/UrlContext';
import { sanitiseLocale } from '../../../../i18n/config';
import { useTrackNavigationClicked } from '../../../../utils/analytics';
import { isGenAiLogomakerAvailable } from '../../../../utils/featureAvailability';
import { toSentenceCase } from '../../../../utils/helper';
import styles, { stylesheet } from './Hero.module.scss';
import type { Design } from '../../content/logo-inspiration';

interface HeroProp {
    design?: Design;
    keywordLongName?: string;
    keywordName?: string;
}

const Hero = ({ design, keywordLongName, keywordName }: HeroProp) => {
    useStyles(stylesheet);
    const { i18n } = useTranslation();
    const { logoMakerUrl, logoDesignUrl } = useUrls();
    const locale = sanitiseLocale(i18n.language);

    const logomakerCtaRef = useTrackNavigationClicked(
        'Logo Ideas',
        'Hero',
        logoMakerUrl ?? '',
        'createYourFreelogo',
    );

    const logoDesignCtaRef = useTrackNavigationClicked(
        'Logo Ideas',
        'Hero',
        logoDesignUrl ?? '',
        'exploreYourOptions',
    );

    return (
        <Banner>
            <BannerTextContainer className={styles.bannerTextContainer}>
                <BannerText>
                    <BannerTitle as="h1">
                        {keywordLongName
                            ? `${toSentenceCase(keywordLongName)} logos`
                            : 'Logo Ideas'}
                    </BannerTitle>
                    <BannerDescription as="p">
                        {isGenAiLogomakerAvailable(locale)
                            ? `From simple and functional to daring and distinct, get inspired by these unique ${keywordName ? keywordName + ' ' : ''}logos. Get started with our free AI Logomaker or work with a professional designer today.`
                            : `From simple and functional to daring and distinct, get inspired by these unique ${keywordName ? keywordName + ' ' : ''}logos. Get started with our free Logomaker or work with a professional designer today.`}
                    </BannerDescription>
                    <BannerLinks>
                        <Button
                            skin="primary"
                            render={(props) => (
                                <Link
                                    href={logoMakerUrl}
                                    className={props.className}
                                    ref={logomakerCtaRef}
                                >
                                    {props.children}
                                </Link>
                            )}
                        >
                            {isGenAiLogomakerAvailable(locale)
                                ? `Create your free AI logo`
                                : `Try our free Logomaker`}
                        </Button>
                        <Button
                            skin="secondary"
                            render={(props) => (
                                <Link
                                    href={logoDesignUrl}
                                    className={props.className}
                                    ref={logoDesignCtaRef}
                                >
                                    {props.children}
                                </Link>
                            )}
                        >
                            Explore all logo services
                        </Button>
                    </BannerLinks>
                </BannerText>
            </BannerTextContainer>
            {design && (
                <BannerImageContainer className={styles.bannerImageContainer}>
                    <BannerImage className={styles.bannerImage}>
                        <SquareImageContainer>
                            <FluidImage
                                src={design.publicCroppedUrl}
                                alt={`${keywordName ? toSentenceCase(`${keywordName} logo`) : 'Logo'} with the title '${design.title}'`}
                            />
                        </SquareImageContainer>
                    </BannerImage>
                </BannerImageContainer>
            )}
        </Banner>
    );
};

export default Hero;
